import { useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Toast } from '@/uiComponents/common';
import { Show } from '@/components';

const AvatarUploader = ({
  value,
  onChange,
  userName,
  disabled = false,
  maxSizeMB = 2,
  allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'],
  minWidth = 100,
  minHeight = 100,
  uploaderEnabled = true,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'inputs' });
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      const extensions = allowedTypes
        .map((type) => '.' + type.split('/')[1].toUpperCase())
        .join(', ');
      setError(t('errors.type', { extensions }));
      inputRef.current.value = '';
      return;
    }

    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > maxSizeMB) {
      setError(t('errors.size', { maxSize: maxSizeMB }));
      inputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        if (img.width > minWidth || img.height > minHeight) {
          setError(t('errors.dimensions', { width: minWidth, height: minHeight }));
          inputRef.current.value = '';
          return;
        }

        onChange(reader.result);
      };
    };

    reader.readAsDataURL(file);
  };

  const handleClick = () => {
    if (uploaderEnabled) inputRef.current?.click();
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <div className='tw-relative'>
      <Avatar className='tw-h-24 tw-w-24 tw-border-4 tw-border-white tw-shadow-lg'>
        <AvatarImage src={value ?? ''} className='tw-object-cover' alt={userName} />
        <AvatarFallback className='tw-bg-blue-500 tw-text-xl tw-text-white'>
          {userName?.charAt(0) || ''}
        </AvatarFallback>
      </Avatar>

      <Show.When isTrue={uploaderEnabled}>
        <Button
          size='icon'
          variant='secondary'
          className='tw-absolute tw--bottom-2 tw--right-2 tw-h-8 tw-w-8 tw-rounded-full tw-shadow-lg'
          onClick={handleClick}
          disabled={disabled || !uploaderEnabled}
        >
          <Camera className='tw-h-4 tw-w-4' />
        </Button>

        {value && (
          <Button
            size='icon'
            variant='ghost'
            className='tw-absolute tw--bottom-1 tw--left-0 tw-h-6 tw-w-6 tw-rounded-full tw-shadow-lg'
            onClick={handleRemove}
          >
            <Trash2 className='tw-h-4 tw-w-4 text-red-500' />
          </Button>
        )}
      </Show.When>

      <input
        type='file'
        accept='image/*'
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {error && <Toast type={'ERROR'} message={error} onCallback={() => setError(null)} />}
    </div>
  );
};

export default AvatarUploader;
